import { Banknote, CalendarX2, CheckCircle2, Landmark, ReceiptText, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";
import type { CashDay } from "@/types/financeiro";

import { StatePill } from "./primitives";
import type { FinanceModal } from "./types";

const RECEIPT_STATE = {
	confirmed: { label: "Confirmado", tone: "green" },
	pending: { label: "Falta comprovante", tone: "amber" },
	reversed: { label: "Estornado", tone: "coral" },
	cancelled: { label: "Cancelado", tone: "muted" },
} as const;

function quantity(count: number, singular: string, plural: string) {
	if (count === 1) {
		return `${count} ${singular}`;
	}

	return `${count} ${plural}`;
}

export function CashDayDetails({
	data,
	view,
	onClose,
	onModal,
}: {
	data: CashDay;
	view: "pending" | "receipts";
	onClose: () => void;
	onModal: (modal: FinanceModal) => void;
}) {
	const pendingCount = data.missing_attendances.length + data.unidentified_incomes.length;
	const title = { pending: "Falta registrar", receipts: "Registrados" };

	return (
		<OverlayShell label={title[view]} onClose={onClose} className="items-center justify-center p-4">
			<div className="overlay-panel max-w-3xl @container">
				<header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-3">
					<p className="text-sm text-muted-foreground">{format.date(data.date)}</p>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label="Fechar recebimentos"
						onClick={onClose}
					>
						<X />
					</Button>
				</header>
				<div className="min-h-0 overflow-y-auto p-5">
					{view === "pending" && (
						<section className="min-w-0">
							<div className="flex items-baseline justify-between gap-3">
								<h2 className="type-section-title text-foreground">Falta registrar</h2>
								<p className="text-xs text-muted-foreground">
									{quantity(pendingCount, "item", "itens")}
								</p>
							</div>

							{pendingCount === 0 && (
								<div className="mt-3 rounded-md border border-info/25 bg-info/10">
									<EmptyState icon={CheckCircle2} title="Nada pendente neste dia" />
								</div>
							)}

							{pendingCount > 0 && (
								<div className="mt-3 grid gap-2">
									{data.missing_attendances.map((attendance) => (
										<article
											key={`attendance:${attendance.id}`}
											className="flex min-w-0 flex-col gap-3 border-b border-border py-4 last:border-b-0 @2xl:flex-row @2xl:items-center"
										>
											<CalendarX2 className="size-4 shrink-0 text-warning" />
											<div className="min-w-0 flex-1">
												<p className="text-sm font-semibold text-foreground">
													{attendance.person_name}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">
													{attendance.service_type}
												</p>
												<p className="text-xs text-muted-foreground">
													{format.time(attendance.start_at)}
												</p>
											</div>
											<Button
												size="sm"
												variant="outline"
												onClick={() =>
													onModal({
														kind: "receipt",
														initial: {
															person_id: attendance.person_id,
															person_name: attendance.person_name,
															attendance_id: attendance.id,
															received_on: data.date,
															description: attendance.service_type,
														},
													})
												}
											>
												Registrar recebimento
											</Button>
										</article>
									))}

									{data.unidentified_incomes.map((income) => (
										<article
											key={`bank:${income.id}`}
											className="flex min-w-0 flex-col gap-3 border-b border-border py-4 last:border-b-0 @2xl:flex-row @2xl:items-center"
										>
											<Landmark className="size-4 shrink-0 text-warning" />
											<div className="min-w-0 flex-1">
												<p className="break-words text-sm font-semibold text-foreground">
													{income.suggested_person_name ??
														income.payer_name ??
														income.description ??
														"Entrada bancária"}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">
													Entrada sem recebimento vinculado
												</p>
											</div>
											<p className="shrink-0 font-mono text-sm font-semibold text-success">
												{FinanceiroLabels.money(income.amount_cents)}
											</p>
											<Button
												size="sm"
												variant="outline"
												onClick={() => onModal({ kind: "bank-resolution", id: income.id })}
											>
												Resolver entrada
											</Button>
										</article>
									))}
								</div>
							)}
						</section>
					)}
					{view === "receipts" && (
						<section className="min-w-0">
							<div className="flex items-baseline justify-between gap-3">
								<h2 className="type-section-title text-foreground">Registrados</h2>
								<p className="text-xs text-muted-foreground">
									{quantity(data.receipts.length, "recebimento", "recebimentos")}
								</p>
							</div>

							{data.receipts.length === 0 && (
								<div className="surface-panel mt-3">
									<EmptyState icon={ReceiptText} title="Nenhum recebimento registrado" />
								</div>
							)}

							{data.receipts.length > 0 && (
								<div className="mt-3 grid gap-2">
									{data.receipts.map((receipt) => {
										const state = RECEIPT_STATE[receipt.state];
										const automaticEvidence =
											(receipt.bank_transaction_id !== null || receipt.card_sale_id !== null) &&
											receipt.evidence_linked_by?.endsWith(":auto") === true;

										return (
											<article
												key={receipt.id}
												className="flex min-w-0 flex-col gap-3 border-b border-border py-4 last:border-b-0 @2xl:flex-row @2xl:items-center"
											>
												<Banknote className="size-4 shrink-0 text-primary" />
												<div className="min-w-0 flex-1">
													<p className="text-sm font-semibold text-foreground">
														{receipt.person_name}
													</p>
													<p className="mt-1 text-xs text-muted-foreground">
														{FinanceiroLabels.receipt.method[receipt.method]}
													</p>
												</div>
												<p className="shrink-0 font-mono text-sm font-semibold text-foreground">
													{FinanceiroLabels.money(receipt.amount_cents)}
												</p>
												<div className="flex flex-wrap gap-1">
													<StatePill label={state.label} tone={state.tone} />
													{automaticEvidence && (
														<StatePill label="Vínculo automático" tone="muted" />
													)}
												</div>
											</article>
										);
									})}
								</div>
							)}
						</section>
					)}
				</div>
			</div>
		</OverlayShell>
	);
}
